import ChartViewer from 'components/ChartViewer';
import CustomiseRentalMarketTrends from 'components/CustomiseRentalMarketTrends';
import CustomiseSchoolMapContainer from 'components/CustomiseSchoolMapContainer';
import 'components/CustomiseSuburbProfile.scss';
import {DragHandle} from 'components/DragHandle';
import EditableTitle from 'components/EditableTitle';
import OptionalTitle from 'components/OptionalTitle';
import SuburbProfileMapContainer from 'components/SuburbProfileMapContainer';
import decreaseIcon from 'images/down_red.svg';
import houseImg from 'images/house.svg';
import insideSchoolCatchment from 'images/inside_school_catchment.svg';
import outsideSchoolCatchment from 'images/outside_school_catchment.svg';
import populationImg from 'images/population.svg';
import populationDownImg from 'images/population_down.svg';
import populationSameImg from 'images/population_same.svg';
import populationUpImg from 'images/population_up.svg';
import upImg from 'images/up.svg';
import increaseIcon from 'images/up_green.svg';
import Formatter from 'lib/Formatter';
import {isNZLocale} from 'lib/PropertyUtils';
import {checkSuburbInsights} from 'lib/ValidationUtils';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import numeral from 'numeral';
import PropTypes from 'prop-types';
import {Component} from 'react';
import {SortableContainer, SortableElement} from 'react-sortable-hoc';
import {Checkbox, Divider, Header, List, Segment} from 'semantic-ui-react';

export const SortableTitle = SortableElement(({value, index, checked, onChange, onChecked}) => {
  return (
    <List.Item key={index} className="sortable-title">
      <DragHandle/>
      <EditableTitle
          onUpdateData={({title}) => onChange(title)}
          isEnabled={checked}
          maxLength={32}
          title={value}
          resizable={false}
      />
      <Checkbox checked={checked} onChange={(e, {checked: newChecked}) => onChecked(newChecked)}/>
    </List.Item>
  );
});

export const SortableTitles = SortableContainer(({section, onUpdateSectionValue, ...props}) => {
  return (
    <List {...props}>
      {section.sectionData.order.map((name, i) => (
        <SortableTitle
          className={name}
          value={section.sectionData[`${name}Title`]}
          checked={section.sectionData[`${name}_isEnabled`] === 'true'}
          index={i}
          key={name}
          onChange={value => onUpdateSectionValue(value || section.defaultTitles[name], `sectionData.${name}Title`)}
          onChecked={value => onUpdateSectionValue(`${value}`, `sectionData.${name}_isEnabled`)}
        />
      ))}
    </List>
  );
});

class CustomiseSuburbProfile extends Component {

  getPopulationChangeImages(populationChange) {
    const data = {
      up: {image: populationUpImg, rotate: 0},
      down: {image: populationDownImg, rotate: 180},
      same: {image: populationSameImg, rotate: 90, left: 8},
    };

    if (populationChange !== null && typeof populationChange !== 'undefined'
      && ['0.000', '-0.000'].indexOf(populationChange.toFixed(3)) !== -1) {
      return data.same;
    }
    if (populationChange > 0) {
      return data.up;
    }
    return data.down;
  }

  createHouseholdColumn(className, header, data) {
    return (<div className={`${className} column`}>
      <div className="column-header">{header}</div>
      <div className="column-subheader">
        <div className="left">TYPE</div>
        <div className="right">%</div>
      </div>
      {data && data.map((v, idx) => {
        return (<div className="row" key={idx}><span className="left">{v.shortDescription}</span><span
          className="right">{v.percentageValue}</span></div>);
      })}
    </div>);
  }

  createColumn(className, header, data, key) {
    return (
      <div className={`${className} column`}>
        <div className="column-subheader">{header}</div>
        {data && data.map((v, idx) => {
          return <div className="row" key={idx}>{v !== null && v[key] ? v[key] : '-'}</div>;
        })}
      </div>
    );
  }

  createPriceColumn(className, header, data, key) {
    return (
      <div className={`${className} column`}>
        <div className="column-subheader">{header}</div>
        {data && data.map((v, idx) => {
          return <div className="row" key={idx}>{v !== null && v[key] ? Formatter.currency(v[key]) : '-'}</div>;
        })}
      </div>
    );
  }

  createAskingRentColumn(className, header, data, key) {
    if (!isNZLocale())
      return this.createPriceColumn(className, header, data, key);
    return '';
  }

  createPercentColumn(className, header, data, key) {
    return (
      <div className={`${className} column`}>
        <div className="column-subheader">{header}</div>
        {data && data.map((v, idx) => {
          return (<div className="row" key={idx}>
            {v && v[key] && v[key] - 1 !== 0 ? Formatter.percentage(v[key] - 1) : '0.0%'}
            {' '}
            {/* the 1.0005 and 0.9995 are the bounds where we consider it flat growth */}
            {v !== null && v[key] && (v[key] >= 1.0005 || v[key] < 0.9995) ? (
              <img src={v[key] > 1 ? increaseIcon : decreaseIcon} alt=""/>
            ) : null}
          </div>);
        })}
      </div>
    );
  }

  createHeaderRow() {
    const nzSuffix = isNZLocale() ? '-nz' : '';
    return (
      <div className="school-table-header">
        <div className={`header school-address${nzSuffix}`}>SCHOOL{!isNZLocale() ? ' ADDRESS' : ''}</div>
        <div className={`header school-distance${nzSuffix}`}>DISTANCE</div>
        <div className={`header school-type${nzSuffix}`}>SCHOOL TYPE</div>
        {!isNZLocale() && <div className="header school-gender">GENDER</div>}
        <div className={`header school-sector${nzSuffix}`}>SECTOR</div>
        <div className={`header school-years${nzSuffix}`}>YEARS</div>
      </div>
    );
  }

  createDataRow(school, index) {
    let image;
    let alt;
    const nzSuffix = isNZLocale() ? '-nz' : '';

    if (school.insideSchoolCatchment) {
      image = insideSchoolCatchment;
      alt = 'Inside School Catchment';
    } else {
      image = outsideSchoolCatchment;
      alt = 'Outside School Catchment';
    }

    return (
      <div className="school-table-row" key={index}>
        <div className={`cell school-address${nzSuffix}`}>
          <img className="left" src={image} alt={alt}/>
          <div className="right">
            <div className="school-name">{school.schoolName}</div>
            {!isNZLocale() && <div className="address">{school.singleLineAddress}</div>}
          </div>
        </div>
        <div className={`cell school-distance${nzSuffix}`}>{school.distance || '-'}</div>
        <div className={`cell school-type${nzSuffix}`}>{school.schoolType || '-'}</div>
        {!isNZLocale() && <div className="cell school-gender">{school.schoolGender || '-'}</div>}
        <div className={`cell school-sector${nzSuffix}`}>{school.schoolSector || '-'}</div>
        <div className={`cell school-years${nzSuffix}`}>{school.years || '-'}</div>
      </div>
    );
  }

  createDemographicsSection(section, suburbName, suburbInsights, onUpdateSectionValue) {
    const populationChange = suburbInsights.populationChange;
    const populationChangeImages = this.getPopulationChangeImages(populationChange);
    let format = '0.0 a';
    if (suburbInsights.population && (suburbInsights.population < 1000 || (suburbInsights.population % 1000) === 0)) {
      format = '0 a';
    }
    const population = numeral(suburbInsights.population).format(format);
    return (
      <Segment className="demographics" style={ {padding: '30px'} }>
        <Header className="section-type" as="h2" style={ {fontWeight: 'bold'} } disabled>{ suburbName }</Header>
        <OptionalTitle
          className="section-title"
          onUpdateData={ ({title, isEnabled}) => {
            if (title !== undefined) {
              onUpdateSectionValue(title || section.defaultTitles.demographic, 'sectionData.demographicTitle');
            }
            if (isEnabled !== undefined) {
              onUpdateSectionValue(`${ isEnabled }`, 'sectionData.demographic_isEnabled');
            }
          } }
          title={ section.sectionData.demographicTitle }
          isEnabled={ section.sectionData.demographic_isEnabled === 'true' }/>
        <div className="map-container">
          <SuburbProfileMapContainer/>
          <div className="insights">
            <Header className="title" as="h2" style={ {fontWeight: 'bold'} }>Suburb Insights</Header>
            <div className="population">
              <img src={ populationImg } alt="population"/>
              { suburbInsights.population ? population : '-' }
              <span>Population</span>
            </div>
            <Divider/>
            { populationChange !== null && typeof populationChange !== 'undefined' && <div className="population-change">
              <img src={ populationChangeImages.image } alt="population change"/>
              <div>
                { numeral(Math.abs(populationChange)).format('0.0%') }
                <img src={ upImg }
                     style={ {
                       transform: `rotate(${ populationChangeImages.rotate }deg)`,
                       marginLeft: populationChangeImages.left
                     } }
                     alt="change" width="8" className="direction"/>
              </div>
              <span>5 year population change</span>
              <Divider/>
            </div>
            }
            <div className="avg-length-ownership">
              <img src={ houseImg } alt="average length of ownership"/>
              <div>{ suburbInsights.averageLengthOfOwnership ? `${ suburbInsights.averageLengthOfOwnership.toFixed(1).replace(/\.0$/, '') } years` : '-' }</div>
              <span>Average length of ownership</span>
            </div>
          </div>
        </div>
        { suburbInsights.censusSummaryDescription && <div className="census-summary">
          { suburbInsights.censusSummaryDescription }
        </div> }
        <div className="demographic-table">
          { this.createHouseholdColumn('household-structure', 'HOUSEHOLD STRUCTURE', suburbInsights.householdStructures) }
          { this.createHouseholdColumn('household-occupancy', isNZLocale() ? 'OCCUPATION' : 'HOUSEHOLD OCCUPANCY', suburbInsights.householdOccupancy) }
          { this.createHouseholdColumn('household-income', 'HOUSEHOLD INCOME', suburbInsights.householdIncomes) }
          { this.createHouseholdColumn('age', 'AGE', suburbInsights.householdAges) }
        </div>
      </Segment>
    );
  }

  createLocalSchoolsSection(section, suburbInsights, onUpdateSectionValue) {
    return (
      <Segment className="local-schools" style={{padding: '30px'}}>
        <OptionalTitle
          className="section-title"
          onUpdateData={({title, isEnabled}) => {
            if (title !== undefined) {
              onUpdateSectionValue(title, 'sectionData.localSchoolsTitle');
            }
            if (isEnabled !== undefined) {
              onUpdateSectionValue(`${isEnabled}`, 'sectionData.localSchools_isEnabled');
            }
          }}
          title={section.sectionData.localSchoolsTitle}
          isEnabled={section.sectionData.localSchools_isEnabled === 'true'}/>
        <CustomiseSchoolMapContainer/>
        <div className="school-table">
          {this.createHeaderRow()}
          {!isEmpty(suburbInsights.schoolData) && suburbInsights.schoolData.map((s, idx) => this.createDataRow(s, idx))}
        </div>
        <div className="legend">
          <div className="inside-catchment"><img src={insideSchoolCatchment} alt="Inside School Catchment"/>
            <div>Property is within school catchment area</div>
          </div>
          <div><img src={outsideSchoolCatchment} alt="Outside School Catchment"/>
            <div>Property is outside school catchment area</div>
          </div>
        </div>
      </Segment>
    );
  }

  createRecentMarketTrendsSection(propertyType, section, suburbInsights, onUpdateSectionValue) {
    return (
      <Segment className="recent-market-trends" style={ {padding: '30px'} }>
        <OptionalTitle
          className="section-title"
          onUpdateData={ ({title, isEnabled}) => {
            if (title !== undefined) {
              onUpdateSectionValue(title || section.defaultTitles.recentMarketTrends, 'sectionData.recentMarketTrendsTitle');
            }
            if (isEnabled !== undefined) {
              onUpdateSectionValue(`${ isEnabled }`, 'sectionData.recentMarketTrends_isEnabled');
            }
          } }
          title={ section.sectionData.recentMarketTrendsTitle }
          isEnabled={ section.sectionData.recentMarketTrends_isEnabled === 'true' }/>
        <ChartViewer base64EncodedPngChartImage={suburbInsights.medianValueChartImage}
                     altText="Median Value chart"
                     errorMessage="Median Value chart is not available because there is not enough data for a statistically reliable output"/>
        <div className="recent-median-values-table">
          { this.createColumn('period', 'PERIOD', suburbInsights.medianValueData, 'period') }
          { this.createColumn('properties-sold', 'PROPERTIES SOLD', suburbInsights.medianValueData, 'propertiesSold') }
          { this.createPriceColumn('median-price', 'MEDIAN VALUE', suburbInsights.medianValueData, 'medianPrice') }
          { this.createPercentColumn('growth', 'GROWTH', suburbInsights.medianValueData, 'growth') }
          { this.createColumn('dom', 'DAYS ON MARKET', suburbInsights.medianValueData, 'dom') }
          { this.createColumn('listings', 'LISTINGS', suburbInsights.medianValueData, 'listings') }
          { this.createAskingRentColumn('asking-rent', 'ASKING RENT', suburbInsights.medianValueData, 'askingRent') }
        </div>
        { !isNZLocale() &&
        <Header className="sales-by-price-header">Sale by Price - 12 Months ({ capitalize(propertyType) })</Header> }
        { !isNZLocale() &&
        <div className="sales-by-price">
          <div className="sales-by-price-table">
            <div className="row">
              <span className="column-subheader left">PRICE</span>
              <span className="column-subheader right">NUMBER</span>
            </div>
            { suburbInsights.salesByPrice?.map((sp) => (
              <div className="row" key={ sp.price }>
                <span className="value left">{ sp.price }</span>
                <span className="value right">{ sp.numberOfSales }</span>
              </div>
            )) }
          </div>
          <ChartViewer base64EncodedPngChartImage={suburbInsights.salesByPriceChartImage}
                       altText="Sales by price chart"
                       errorMessage="Sales by Price chart is not available because there is not enough data for a statistically reliable output"/>
        </div>
        }
      </Segment>
    );
  }

  createLongTermMarketTrendsSection(section, suburbInsights, onUpdateSectionValue) {
    return (
      <Segment className="long-term-market-trends" style={ {padding: '30px'} }>
        <OptionalTitle
          className="section-title"
          onUpdateData={ ({title, isEnabled}) => {
            if (title !== undefined) {
              onUpdateSectionValue(title, 'sectionData.longTermMarketTrendsTitle');
            }
            if (isEnabled !== undefined) {
              onUpdateSectionValue(`${ isEnabled }`, 'sectionData.longTermMarketTrends_isEnabled');
            }
          } }
          title={ section.sectionData.longTermMarketTrendsTitle }
          isEnabled={ section.sectionData.longTermMarketTrends_isEnabled === 'true' }/>
        <ChartViewer base64EncodedPngChartImage={suburbInsights.longTermMedianSalesPriceChartImage}
                     altText="Median Sales Price chart"
                     errorMessage="Median Sales Price chart is not available because there is not enough data for a statistically reliable output"/>
        <div className="long-term-median-values-table">
          { this.createColumn('period', 'PERIOD', suburbInsights.longTermMedianValueData, 'period') }
          { this.createColumn('properties-sold', 'PROPERTIES SOLD', suburbInsights.longTermMedianValueData, 'propertiesSold') }
          { this.createPriceColumn('median-price', 'MEDIAN VALUE', suburbInsights.longTermMedianValueData, 'medianPrice') }
          { this.createPercentColumn('growth', 'GROWTH', suburbInsights.longTermMedianValueData, 'growth') }
          { this.createColumn('dom', 'DAYS ON MARKET', suburbInsights.longTermMedianValueData, 'dom') }
          { this.createColumn('listings', 'LISTINGS', suburbInsights.longTermMedianValueData, 'listings') }
          { this.createAskingRentColumn('asking-rent', 'ASKING RENT', suburbInsights.longTermMedianValueData, 'askingRent') }
        </div>
      </Segment>
    );
  }


  marketTrendsData(marketTrendsType, suburbInsights) {
    return {
      medianAskingRentChartImage: suburbInsights[`${marketTrendsType}MedianAskingRentChartImage`],
      grossRentalYieldChartImage: suburbInsights[`${marketTrendsType}GrossRentalYieldChartImage`],
      medianAskingRentData: suburbInsights[`${marketTrendsType}MedianAskingRentData`],
    };
  }

  render() {
    let {propertyType, reportType, section, suburbName, suburbInsights, onUpdateSectionValue} = this.props;

    if (!checkSuburbInsights({ suburbInsights })) {
      return null;
    }

    const showMarketTrends = reportType === 'rental';
    const recentMarketTrendsData = this.marketTrendsData('recent', suburbInsights);
    const longTermMarketTrendsData = this.marketTrendsData('longTerm', suburbInsights);
    return (
      <div className="CustomiseSuburbProfile">
        <Header className="section-type" as="h2" style={ {fontWeight: 'bold'} } disabled>Suburb Profile</Header>
        <Segment className="help-text" style={ {padding: '30px'} }>
          <p>Includes four sections that help explain the suburb and current market conditions:</p>
          <ul>
            <li>Demographic: Suburb map, information about the suburb and household breakdown.</li>
            <li>Local Schools: A map with nearby local schools and information about each school.</li>
            <li>Recent Market Trends: Contains Market Trend information with our most recent 12 months of data.</li>
            <li>Long Term Market Trends: Contains Market Trend information with the last 20 years of data.</li>
          </ul>
          <p>You can change the order of each page below, or untick to hide.</p>
          <SortableTitles className="sortable-titles"
                          section={ section }
                          disableAutoscroll
                          lockAxis="y"
                          onSortEnd={ ({oldIndex, newIndex}) => {
                            const next = [...section.sectionData.order];
                            next.splice(oldIndex, 1);
                            next.splice(newIndex, 0, section.sectionData.order[oldIndex]);
                            onUpdateSectionValue(next, 'sectionData.order');
                          } }
                          onUpdateSectionValue={ onUpdateSectionValue }
                          useDragHandle
          />
        </Segment>

        { this.createDemographicsSection(section, suburbName, suburbInsights, onUpdateSectionValue) }
        { this.createLocalSchoolsSection(section, suburbInsights, onUpdateSectionValue) }
        { showMarketTrends
          ? <CustomiseRentalMarketTrends marketTrendsData={ recentMarketTrendsData } marketTrendsType="recent"
                                         section={ section } onUpdateSectionValue={ onUpdateSectionValue }/>
          : this.createRecentMarketTrendsSection(propertyType, section, suburbInsights, onUpdateSectionValue)
        }
        { showMarketTrends
          ? <CustomiseRentalMarketTrends marketTrendsData={ longTermMarketTrendsData } marketTrendsType="longTerm"
                                         section={ section } onUpdateSectionValue={ onUpdateSectionValue }/>
          : this.createLongTermMarketTrendsSection(section, suburbInsights, onUpdateSectionValue)
        }
      </div>
    );
  }
}

CustomiseSuburbProfile.propTypes = {
  propertyType: PropTypes.string.isRequired,
  reportType: PropTypes.string.isRequired,
  section: PropTypes.object.isRequired,
  suburbName: PropTypes.string.isRequired,
  suburbInsights: PropTypes.shape({
    population: PropTypes.number,
    populationChange: PropTypes.number,
    averageLengthOfOwnership: PropTypes.number,
    censusSummaryDescription: PropTypes.string,
    medianValueChartUrl: PropTypes.string,
    medianValueData: PropTypes.arrayOf(PropTypes.shape({
      period: PropTypes.string.isRequired,
      propertiesSold: PropTypes.number,
      medianPrice: PropTypes.number,
      growth: PropTypes.number,
      dom: PropTypes.number,
      listings: PropTypes.number,
      askingRent: PropTypes.number
    })),
    longTermMedianValueData: PropTypes.arrayOf(PropTypes.shape({
      period: PropTypes.string.isRequired,
      propertiesSold: PropTypes.number,
      medianPrice: PropTypes.number,
      growth: PropTypes.number,
      dom: PropTypes.number,
      listings: PropTypes.number,
      askingRent: PropTypes.number
    })),
    longTermMedianSalesPriceChartUrl: PropTypes.string,
    householdOccupancy: PropTypes.arrayOf(PropTypes.shape({
      shortDescription: PropTypes.string.isRequired,
      percentageValue: PropTypes.number.isRequired,
    }).isRequired),
    householdStructures: PropTypes.arrayOf(PropTypes.shape({
      shortDescription: PropTypes.string.isRequired,
      percentageValue: PropTypes.number.isRequired,
    }).isRequired),
    householdIncomes: PropTypes.arrayOf(PropTypes.shape({
      shortDescription: PropTypes.string.isRequired,
      percentageValue: PropTypes.number.isRequired,
    }).isRequired),
    householdAges: PropTypes.arrayOf(PropTypes.shape({
      shortDescription: PropTypes.string.isRequired,
      percentageValue: PropTypes.number.isRequired,
    }).isRequired),
    salesByPrice: PropTypes.arrayOf(PropTypes.shape({
      price: PropTypes.string.isRequired,
      numberOfSales: PropTypes.number.isRequired,
    }).isRequired),
  }),
  onUpdateSectionValue: PropTypes.func.isRequired,
};

export default CustomiseSuburbProfile;